import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router";

import { ROUTES } from "@/app/routes";
import { ApiError } from "@/core/api/errors";
import { IdentityError } from "@/core/auth/identity";
import { useSession } from "@/core/auth/session";
import { log } from "@/core/log";
import { ThemeSwitch } from "@/core/theme/ThemeSwitch";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { EnvironmentTag } from "@/ui/environment-tag";
import { LogoMark } from "@/ui/logo";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"
      />
      <path fill="#FBBC05" d="M6.4 14c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V7.4H3.1a10 10 0 0 0 0 9.2z" />
      <path
        fill="#EA4335"
        d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.4L6.4 10C7.2 7.7 9.4 5.9 12 5.9z"
      />
    </svg>
  );
}

/** What went wrong, in words for people; nothing for a popup the user closed. */
function messageFor(error: unknown): string | null {
  if (error instanceof IdentityError) return error.reason === "cancelled" ? null : error.message;
  if (error instanceof ApiError) {
    return error.status === 401 ? "Your sign-in took too long. Please sign in again." : error.message;
  }
  log.warn("Sign-in failed", error);
  return "Sign-in didn't work. Please try again.";
}

export function SignInPage() {
  const session = useSession();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState<"google" | "email" | "reset" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const next = new URLSearchParams(location.search).get("next");
  if (session.status === "signed-in") {
    return <Navigate to={next?.startsWith("/") ? next : ROUTES.overview} replace />;
  }

  const run = async (kind: "google" | "email", action: () => Promise<void>) => {
    setPending(kind);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (failure) {
      setError(messageFor(failure));
    } finally {
      setPending(null);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void run("email", () => session.signInWithEmail(email.trim(), password));
  };

  const onForgot = async () => {
    if (!email.trim()) {
      setError("Type your email above first, then choose “Forgot?”.");
      return;
    }
    setPending("reset");
    setError(null);
    try {
      await session.sendPasswordReset(email.trim());
      setNotice(`If ${email.trim()} has an account, a reset link is on its way.`);
    } catch (failure) {
      setError(messageFor(failure));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-2">
      <section className="hidden flex-col justify-between bg-brand-deep p-14 text-white lg:flex">
        <div className="flex items-center gap-3">
          <LogoMark tile />
          <span className="text-[22px] font-extrabold">Brocoly Admin</span>
          <EnvironmentTag />
        </div>
        <div className="flex max-w-[460px] flex-col gap-4">
          <p className="text-[44px] leading-[1.1] font-extrabold tracking-tight">
            Every kitchen, every plate, one place.
          </p>
          <p className="text-[17px] leading-relaxed text-brand-mist">
            Brands, subscriptions, disputes and payouts for the whole Brocoly marketplace.
          </p>
        </div>
        <span className="text-[13px] text-brand-mist-2">Admin portal · for the Brocoly team only</span>
      </section>

      <section className="flex flex-col bg-surface">
        <div className="flex items-center justify-between gap-3 px-6 py-4 lg:justify-end lg:px-4">
          <div className="flex min-w-0 items-center gap-3 lg:hidden">
            <LogoMark />
            <span className="truncate text-lg font-extrabold">Brocoly Admin</span>
            <EnvironmentTag />
          </div>
          <ThemeSwitch />
        </div>
        <div className="flex grow items-center justify-center px-6 pb-16">
          <div className="flex w-full max-w-[400px] flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-[30px] font-extrabold tracking-tight">Sign in</h1>
              <p className="text-[15px] text-muted">Use your Brocoly team account.</p>
            </div>

            {session.status === "unavailable" && (
              <p role="alert" className="rounded-xl bg-warn-soft p-3 text-sm text-warn">
                Can't reach Brocoly right now.{" "}
                <button type="button" className="font-bold underline" onClick={session.retry}>
                  Try again
                </button>
              </p>
            )}

            <Button
              variant="secondary"
              size="lg"
              loading={pending === "google"}
              disabled={pending !== null}
              onClick={() => void run("google", session.signInWithGoogle)}
            >
              {pending !== "google" && <GoogleIcon />}
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 text-[13px] text-faint">
              <span className="h-px grow bg-border" />
              or with email
              <span className="h-px grow bg-border" />
            </div>

            <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
              <Field
                label="Email"
                type="email"
                autoComplete="username"
                inputMode="email"
                placeholder="you@brocoly.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Field
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                labelAside={
                  <button
                    type="button"
                    className="text-[13px] font-semibold text-link hover:underline disabled:opacity-60"
                    onClick={() => void onForgot()}
                    disabled={pending !== null}
                  >
                    Forgot?
                  </button>
                }
              />
              <Button
                type="submit"
                size="lg"
                loading={pending === "email"}
                disabled={pending !== null || !email || !password}
              >
                Sign in
              </Button>
            </form>

            {error && (
              <p role="alert" className="rounded-xl bg-danger-soft p-3 text-sm font-medium text-danger">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="rounded-xl bg-primary-soft p-3 text-sm text-primary-soft-ink">
                {notice}
              </p>
            )}
            <p className="text-center text-[13px] text-muted">
              Using a shared device? Sign out when you're done.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
